/**
 * Environment configuration helper
 * Determines the current environment and provides appropriate configuration values
 */

export type Environment = 'development' | 'production';

/**
 * Get the current environment based on Vite's mode
 * In development: import.meta.env.MODE === 'development'
 * In production build: import.meta.env.MODE === 'production'
 */
// Injected by vite.config.ts `define` option — replaced with a literal string at build time
declare const __BUILD_MODE__: string;

export function getCurrentEnvironment(): Environment {
  return __BUILD_MODE__ === 'production' ? 'production' : 'development';
}

/**
 * Get environment-specific configuration values
 */
export function getEnvironmentConfig() {
  const env = getCurrentEnvironment();
  const isDev = env === 'development';

  return {
    environment: env,
    environmentId: isDev
      ? import.meta.env.VITE_ENVIRONMENT_ID_DEV
      : import.meta.env.VITE_ENVIRONMENT_ID_PROD,
    apiBaseUrl: isDev
      ? import.meta.env.VITE_API_BASE_URL_DEV
      : import.meta.env.VITE_API_BASE_URL_PROD,
    apiDefinition: isDev
      ? import.meta.env.VITE_API_DEFINITION_DEV
      : import.meta.env.VITE_API_DEFINITION_PROD,
    // BFF API remains the same for now (can be extended later if needed)
    bffApiBaseUrl: import.meta.env.VITE_VOLTBOT_API_BASE_URL,
    bffApiDefinition: import.meta.env.VITE_BFF_API_DEFINITION,
    authBridgeUrl: isDev
      ? import.meta.env.VITE_AUTHBRIDGE_URL_DEV
      : import.meta.env.VITE_AUTHBRIDGE_URL_PROD
  };
}

/**
 * Get a specific environment variable with fallback
 */
export function getEnvVar(key: string, fallback?: string): string | undefined {
  return import.meta.env[key] || fallback;
}
